import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, Loader2, Sparkles, FileText, History as HistoryIcon, Trash2, ChevronRight, Upload, FileUp, Pencil, ShieldCheck, X, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../components/ui/pagination';
import { AppShell } from '../components/Layout';
import { linkedge } from '../lib/api';
import { LinkedInExportGuide } from '../components/dashboard/LinkedInExportGuide';
import { PlanHintBanner } from '../components/dashboard/PlanHintBanner';
import BlayzBot from '../components/dashboard/BlayzBot';
import BrandedReportButton from '../components/results/BrandedReportButton';
import ScoreTimeline from '../components/dashboard/ScoreTimeline';
import { getErrorMessage } from '../lib/errors';
import { useAuth } from '../lib/auth-context';
import { toast } from 'sonner';
import { peekPendingFreeAuditPdf, clearPendingFreeAuditPdf } from '../lib/pendingFreeAuditPdf';
import { format } from 'date-fns';
import { clearPendingAudit } from '../lib/billing';

// 12 cards per page = 4 rows × 3 cols on large screens, 6 rows × 2 on tablet,
// 12 rows on mobile. Tuned so the active page fits a single scroll-screen.
const HISTORY_PAGE_SIZE = 12;

// Audit stage/objective vocab — kept in sync with backend
// full_audit_v2.OBJECTIVES_BY_STAGE. Order matters — user-facing.
const AUDIT_OBJECTIVES = {
  // UI segment "Student/Fresher" — merged from backend `student` + `fresher`
  // stages. Every objective maps to a key the backend `fresher` stage
  // accepts (validated in ``full_audit_v2.OBJECTIVES_BY_STAGE``). The
  // remapping to ``fresher`` happens on submit; see STATUS_TO_BACKEND.
  student_fresher: [
    { key: 'recruiter_visibility_first_job', label: 'Recruiter & First Job' },
    { key: 'learning_thought_leadership', label: 'Thought Leadership' },
    { key: 'projects_networking', label: 'Projects & Networking' },
  ],
  // "Founder" — maps to backend ``corporate`` stage; ships three founder-
  // specific goals. Investor Visibility triggers the founder-playbook
  // rewrite (headline formula, About four-beat narrative, Featured
  // curation, metric-driven Experience, banner/photo recs) — see
  // ``full_audit_v2.build_prompt`` + ``_OBJECTIVE_TILTS['investor_visibility']``.
  founder: [
    { key: 'investor_visibility', label: 'Investor Visibility' },
    { key: 'business_development', label: 'Business Development' },
    { key: 'thought_leadership', label: 'Thought Leadership' },
  ],
  corporate: [
    { key: 'recruiter_visibility_career_growth', label: 'Recruiter & Career Growth' },
    { key: 'thought_leadership', label: 'Thought Leadership' },
    { key: 'business_development', label: 'Business Development' },
  ],
};

// UI stage → backend stage. Backend still tracks `student`/`fresher`/`corporate`
// only; the merged/founder UI segments compress to the closest backend stage
// so the audit pipeline continues to receive a valid enum.
const STATUS_TO_BACKEND = {
  student_fresher: 'fresher',
  founder: 'corporate',
  corporate: 'corporate',
};

const STATUS_LABELS = [
  { key: 'student_fresher', label: 'Student/Fresher' },
  { key: 'founder', label: 'Founder' },
  { key: 'corporate', label: 'Corporate' },
];

// Language slider — modulates the About + Experience rewrite tone.
// Auto-picked when the user leaves it on 'auto'. Kept in sync with
// backend full_audit_v2.LANGUAGE_SLIDERS + default_slider_for().
const LANGUAGE_SLIDER_OPTIONS = [
  { key: 'auto', label: 'Auto', help: 'We pick the best fit for your stage + goal.' },
  { key: 'strict', label: 'Strict', help: 'Fact-first, declarative — metric leads every line.' },
  { key: 'balanced', label: 'Balanced', help: 'Light context before the result — plain language.' },
  { key: 'high_storytelling', label: 'High-storytelling', help: 'Setup → tension → turn → result. Needs real detail.' },
];

function defaultSliderFor(status, objective) {
  if (status === 'student_fresher') return 'strict';
  if (status === 'founder') {
    if (objective === 'thought_leadership') return 'high_storytelling';
    if (objective === 'investor_visibility') return 'high_storytelling';
    return 'balanced';
  }
  if (status === 'corporate') {
    if (objective === 'thought_leadership') return 'high_storytelling';
    return 'balanced';
  }
  return 'balanced';
}

function AuditContextPicker({ status, setStatus, objective, setObjective, slider, setSlider, disabled, glow = false }) {
  const objectives = AUDIT_OBJECTIVES[status] || [];
  const autoPick = defaultSliderFor(status, objective);
  const effectiveSlider = slider === 'auto' ? autoPick : slider;
  const autoLabel = LANGUAGE_SLIDER_OPTIONS.find((o) => o.key === autoPick)?.label || 'Balanced';
  return (
    <div
      className={`mt-5 max-w-3xl rounded-2xl border-2 border-primary/25 bg-primary/[0.03] p-4 transition-shadow ${
        glow ? 'attention-glow' : ''
      }`}
      data-testid="dashboard-audit-context"
    >
      <div className="flex items-start gap-4 flex-wrap">
        <div>
          <p className="text-[10px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground mb-1.5">You are</p>
          <div className="inline-flex rounded-full border border-border bg-card/80 backdrop-blur p-0.5" role="tablist">
            {STATUS_LABELS.map((s) => {
              const isActive = status === s.key;
              const activeCls = {
                student_fresher: 'bg-purple-600 text-white shadow-sm',
                founder: 'bg-orange-500 text-white shadow-sm',
                corporate: 'bg-teal-600 text-white shadow-sm',
              }[s.key] || 'bg-primary text-primary-foreground';
              return (
                <button
                  key={s.key}
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    setStatus(s.key);
                    setObjective(AUDIT_OBJECTIVES[s.key][0].key);
                  }}
                  className={`text-[12.5px] px-3 py-1.5 rounded-full transition-all whitespace-nowrap font-medium ${
                    isActive ? activeCls : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid={`dashboard-audit-status-${s.key}`}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex-1 min-w-[260px]">
          <p className="text-[10px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground mb-1.5">Audit goal</p>
          <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar">
            {objectives.map((o) => {
              const isActive = objective === o.key;
              const activeCls = {
                student_fresher: 'border-purple-400 bg-purple-50 text-purple-700 dark:bg-purple-500/10 dark:text-purple-300 dark:border-purple-500/40',
                founder: 'border-orange-400 bg-orange-50 text-orange-700 dark:bg-orange-500/10 dark:text-orange-300 dark:border-orange-500/40',
                corporate: 'border-teal-400 bg-teal-50 text-teal-700 dark:bg-teal-500/10 dark:text-teal-300 dark:border-teal-500/40',
              }[status] || 'border-primary bg-primary/10 text-primary';
              return (
                <button
                  key={o.key}
                  type="button"
                  disabled={disabled}
                  onClick={() => setObjective(o.key)}
                  className={`text-[11.5px] px-2.5 py-1.5 rounded-full border transition-all whitespace-nowrap shrink-0 ${
                    isActive
                      ? activeCls
                      : 'border-border text-muted-foreground hover:text-foreground hover:border-border/70'
                  }`}
                  data-testid={`dashboard-audit-objective-${o.key}`}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <p className="text-[11.5px] text-muted-foreground mt-3">
        Every keyword, headline and recommendation below will align with your selection.
      </p>

      {/* Language slider — modulates About + Experience rewrite tone.
          'Auto' picks the sensible default for the stage + goal; users
          can override to STRICT (fact-first) or HIGH-STORYTELLING
          (Setup→Tension→Turn→Result) for senior POV-heavy profiles. */}
      <div className="mt-4 pt-4 border-t border-border/60" data-testid="dashboard-language-slider">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-[10px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground">Rewrite tone</p>
          {slider === 'auto' && (
            <span className="text-[10.5px] text-muted-foreground/80">
              (Auto = <span className="text-primary font-semibold">{autoLabel}</span> for this pick)
            </span>
          )}
        </div>
        <div className="mt-2 inline-flex flex-wrap gap-1.5">
          {LANGUAGE_SLIDER_OPTIONS.map((o) => {
            const isEffective = o.key === 'auto' ? false : effectiveSlider === o.key && slider !== 'auto';
            const isSelected = slider === o.key;
            return (
              <button
                key={o.key}
                type="button"
                disabled={disabled}
                onClick={() => setSlider(o.key)}
                title={o.help}
                className={`text-[11.5px] px-3 py-1.5 rounded-full border transition-colors whitespace-nowrap ${
                  isSelected
                    ? 'border-primary bg-primary/10 text-primary'
                    : (isEffective
                      ? 'border-primary/40 text-foreground'
                      : 'border-border text-muted-foreground hover:text-foreground hover:border-border/70')
                }`}
                data-testid={`dashboard-language-slider-${o.key}`}
              >
                {o.label}
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-muted-foreground mt-1.5">
          {LANGUAGE_SLIDER_OPTIONS.find((o) => o.key === (slider === 'auto' ? autoPick : slider))?.help}
        </p>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [uploadStage, setUploadStage] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  // Rolling upload messages so users see WHAT is happening at each step
  // during the ~30-60s PDF handoff → parse → context extraction phase.
  // Cycled by a timer while uploadingPdf is true.
  const UPLOAD_STAGES = React.useMemo(() => [
    { label: 'Uploading your PDF…',               sub: 'Sending securely (2-4s).' },
    { label: 'Extracting the text layer…',        sub: 'Reading every page including columns.' },
    { label: 'Parsing headline + About + skills…', sub: 'Anchoring on your main column, ignoring sidebar.' },
    { label: 'Handing off to the auditor…',       sub: 'Your stage + goal are being applied.' },
  ], []);
  React.useEffect(() => {
    if (!uploadingPdf) { setUploadStage(0); return; }
    const t = setInterval(() => {
      setUploadStage((s) => Math.min(UPLOAD_STAGES.length - 1, s + 1));
    }, 5000);
    return () => clearInterval(t);
  }, [uploadingPdf, UPLOAD_STAGES.length]);
  const [activeTab, setActiveTab] = useState('pdf');
  // Objective + status context for the paid audit. The picker applies to
  // both the PDF upload and the manual-wizard paths — the /analyze
  // endpoint uses it to tilt keywords, headline variants and peer
  // benchmarking toward the selected goal.
  const [auditStatus, setAuditStatus] = useState('corporate');
  const [auditObjective, setAuditObjective] = useState('recruiter_visibility_career_growth');
  // Language slider — 'auto' lets the backend pick based on stage+goal;
  // user can override to strict / balanced / high_storytelling.
  const [auditSlider, setAuditSlider] = useState('auto');
  // Track whether the user has explicitly interacted with the audit-goal
  // picker. We glow the picker to draw attention until they touch it — the
  // defaults are only defaults, and letting users blow past the goal
  // selector unknowingly leads to generic outputs.
  const [contextTouched, setContextTouched] = useState(false);

  // ---- 2-step wizard state ------------------------------------------
  // Step 1: pick status + audit goal + rewrite tone (AuditContextPicker)
  // Step 2: upload PDF / open manual wizard + tick consent
  // We advance automatically when the user hits Continue on Step 1; the
  // summary chip in Step 2 lets them jump back with one click.
  const [wizardStep, setWizardStep] = useState(1);

  // Derived label helpers for the Step 2 summary chip.
  const _statusLabel = (STATUS_LABELS.find((s) => s.key === auditStatus) || {}).label || '';
  const _objectiveLabel = (AUDIT_OBJECTIVES[auditStatus] || []).find((o) => o.key === auditObjective)?.label || auditObjective || '';
  const _sliderLabel = (LANGUAGE_SLIDER_OPTIONS.find((s) => s.key === auditSlider) || {}).label || 'Auto';
  const markContextTouched = () => setContextTouched(true);
  // 3-purpose consent state — profile analysis is REQUIRED before the
  // audit is allowed to start. Cohort + marketing are optional.
  // PDF handed off from the free-score hero (same file, no re-upload).
  // Consent for profile_analysis was already granted + logged on the hero,
  // so we honour it here instead of re-asking.
  const [pendingPdf, setPendingPdf] = useState(() => peekPendingFreeAuditPdf());
  const [consent, setConsent] = useState(() => (
    peekPendingFreeAuditPdf()
      ? { analysis: true, cohort: false, marketing: false }
      : { analysis: false, cohort: false, marketing: false }
  ));
  // Blocker dialog — hard popup shown when a user tries to upload/drop
  // a PDF (or open the manual wizard) before ticking status + goal AND
  // the required consent checkbox. Provides a "Take me there" CTA that
  // scrolls the missing field into view + focuses it.
  const [blocker, setBlocker] = useState({ open: false, kind: null });
  const contextPickerRef = useRef(null);
  const consentBoxRef = useRef(null);
  const openBlocker = (kind) => setBlocker({ open: true, kind });
  const focusMissingField = () => {
    setBlocker({ open: false, kind: null });
    setTimeout(() => {
      const target = blocker.kind === 'context' ? contextPickerRef.current : consentBoxRef.current;
      if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.classList.add('ring-4', 'ring-primary/60', 'ring-offset-2');
      setTimeout(() => target.classList.remove('ring-4', 'ring-primary/60', 'ring-offset-2'), 2200);
    }, 100);
  };
  // Central preflight — used by every entry surface (dropzone click,
  // drag-drop, Choose PDF button, manual wizard, pending-PDF card).
  // Returns true when the user is allowed to proceed. When false it
  // opens the blocker popup on the offending field.
  const preflightUpload = () => {
    if (!contextTouched) { openBlocker('context'); return false; }
    if (!consent.analysis) { openBlocker('consent'); return false; }
    return true;
  };
  const [history, setHistory] = useState([]);
  const [historyRole, setHistoryRole] = useState('viewer');
  const fileInputRef = useRef(null);
  const autoStartRef = useRef(false);

  const isAdminOrEditor = user?.role === 'admin' || user?.role === 'editor';

  // Owner filter (e.g., admin drilled in from "/admin" with ?owner=<user_id>)
  const ownerFilter = searchParams.get('owner') || '';
  const clearOwnerFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('owner');
    setSearchParams(next, { replace: true });
  };

  // Resolve filter label from the first matching analysis
  const [hideLegacy, setHideLegacy] = useState(false);
  const filteredHistory = useMemo(() => {
    let items = history;
    if (ownerFilter) items = items.filter((h) => h.user_id === ownerFilter);
    if (hideLegacy) items = items.filter((h) => !h.is_legacy);
    return items;
  }, [history, ownerFilter, hideLegacy]);

  const legacyCount = useMemo(
    () => history.filter((h) => h.is_legacy).length,
    [history],
  );

  // -- Pagination over filteredHistory ------------------------------------
  // Cap the grid to HISTORY_PAGE_SIZE per page (12) so the user always sees a
  // single-screen worth and uses Prev/Next/page-numbers to navigate. Changes
  // to filter or history length snap the page back to 1.
  const [historyPage, setHistoryPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(filteredHistory.length / HISTORY_PAGE_SIZE));
  useEffect(() => {
    if (historyPage > totalPages) setHistoryPage(1);
  }, [historyPage, totalPages]);
  useEffect(() => {
    // Reset to first page when the owner filter changes
    setHistoryPage(1);
  }, [ownerFilter, hideLegacy]);
  const pagedHistory = useMemo(() => {
    const start = (historyPage - 1) * HISTORY_PAGE_SIZE;
    return filteredHistory.slice(start, start + HISTORY_PAGE_SIZE);
  }, [filteredHistory, historyPage]);
  // Build a compact ellipsised page-number list (e.g. 1 ... 4 5 6 ... 12)
  const pageNumbers = useMemo(() => {
    const pages = [];
    const total = totalPages;
    const current = historyPage;
    if (total <= 7) {
      for (let i = 1; i <= total; i += 1) pages.push(i);
      return pages;
    }
    pages.push(1);
    if (current > 4) pages.push('…');
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    for (let i = start; i <= end; i += 1) pages.push(i);
    if (current < total - 3) pages.push('…');
    pages.push(total);
    return pages;
  }, [totalPages, historyPage]);

  const filterLabel = useMemo(() => {
    if (!ownerFilter) return '';
    const match = history.find((h) => h.user_id === ownerFilter);
    return match?.owner_name || match?.owner_email || ownerFilter.slice(0, 10);
  }, [ownerFilter, history]);

  const loadHistory = async () => {
    try {
      const data = await linkedge.history();
      setHistory(data.items || []);
      setHistoryRole(data.role || user?.role || 'viewer');
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    loadHistory();
  }, []);

  // 2026-02 framework pivot: URL-driven audits are retired. If a legacy
  // `pendingAudit` (source=free-score) is still in localStorage we clear
  // it so it doesn't hang around confusing the funnel. No auto-scrape
  // runs anymore — users must upload a PDF post-signup.
  useEffect(() => {
    if (autoStartRef.current) return;
    autoStartRef.current = true;
    try { clearPendingAudit(); } catch { /* ignore */ }
    if (searchParams.get('autostart_url')) {
      const next = new URLSearchParams(searchParams);
      next.delete('autostart_url');
      setSearchParams(next, { replace: true });
    }
  }, []);

  const handlePdfFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please upload a PDF file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('PDF too large (max 10MB)');
      return;
    }
    if (!contextTouched) {
      openBlocker('context');
      return;
    }
    if (!consent.analysis) {
      openBlocker('consent');
      return;
    }
    setUploadingPdf(true);
    const t = toast.loading('Uploading PDF...');
    try {
      const { job_id } = await linkedge.importPdf(file);
      toast.loading('Reading your profile with AI... ~20-40s', { id: t });

      // Poll until ready / failed (up to 3 minutes)
      const start = Date.now();
      const MAX_MS = 180_000;
      while (true) {
        await new Promise((r) => setTimeout(r, 3000));
        let job;
        try {
          job = await linkedge.getImportPdfJob(job_id);
        } catch (pollErr) {
          if (Date.now() - start > MAX_MS) throw pollErr;
          continue;
        }
        if (job.status === 'ready') {
          toast.success('Profile imported. Review and submit.', { id: t });
          navigate('/wizard', { state: { profile: job.profile, fromPdf: true, filename: job.filename, auditContext: { status: STATUS_TO_BACKEND[auditStatus] || auditStatus, objective: auditObjective, language_slider: auditSlider }, consent } });
          return;
        }
        if (job.status === 'failed') {
          throw new Error(job.error || 'Failed to parse PDF');
        }
        if (Date.now() - start > MAX_MS) {
          throw new Error('Parsing took too long. Try a smaller PDF.');
        }
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not parse PDF'), { id: t });
    } finally {
      setUploadingPdf(false);
    }
  };

  const onFilePick = (e) => {
    const file = e.target.files?.[0];
    handlePdfFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    handlePdfFile(file);
  };

  const onDelete = async (id) => {
    try {
      await linkedge.deleteAnalysis(id);
      toast.success('Deleted');
      loadHistory();
    } catch {
      toast.error('Could not delete');
    }
  };

  const statusBadge = (status) => {
    const map = {
      complete: 'bg-[hsl(var(--mint))] text-foreground',
      running: 'bg-[hsl(var(--sky))] text-foreground',
      queued: 'bg-secondary text-foreground',
      failed: 'bg-destructive/15 text-destructive',
    };
    return <Badge className={`${map[status] || 'bg-secondary'} border border-border`}>{status}</Badge>;
  };

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
        {user?.role === 'admin' && (
          <Card className="border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-950 rounded-2xl shadow-sm" data-testid="dashboard-admin-banner">
            <CardContent className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 justify-between">
              <div className="flex items-start gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground shrink-0">
                  <ShieldCheck size={18} />
                </span>
                <div>
                  <p className="font-semibold leading-tight">You&apos;re an admin.</p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Manage users and give your team access from the Admin panel.
                  </p>
                </div>
              </div>
              <Button onClick={() => navigate('/admin')} className="rounded-xl shrink-0" data-testid="dashboard-admin-cta">
                Open Admin panel <ArrowRight size={14} className="ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}
        {/* Plan hint banner (only renders for users without an active plan) */}
        <PlanHintBanner position="top" />
        {/* Hero analyze card */}
        <Card className="relative overflow-hidden border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-950 shadow-sm rounded-2xl">
          <CardContent className="relative p-6 sm:p-8">
            <Badge className="bg-card border border-border text-foreground" data-testid="dashboard-eyebrow">
              <Sparkles size={12} className="mr-1.5" /> New analysis
            </Badge>
            <h1 className="mt-3 text-3xl sm:text-4xl font-bold">Sharpen your LinkedIn.</h1>
            <p className="text-muted-foreground mt-2 max-w-2xl">
              Upload your LinkedIn PDF (More &rarr; Save to PDF) or paste your profile manually.
              We&apos;ll assess it against Blayzrr&apos;s objective-specific Profile Intelligence Framework.
            </p>

            {/* Progress dots — visible affordance for the 2-step flow. */}
            <div className="mt-4 flex items-center gap-3 text-[11px] font-mono-edge uppercase tracking-[0.16em] text-muted-foreground" data-testid="dashboard-wizard-steps">
              <span className={`flex items-center gap-1.5 ${wizardStep === 1 ? 'text-foreground' : ''}`}>
                <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold ${wizardStep >= 1 ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'}`}>1</span>
                Pick your goal
              </span>
              <span className="h-px flex-1 max-w-[60px] bg-border" />
              <span className={`flex items-center gap-1.5 ${wizardStep === 2 ? 'text-foreground' : ''}`}>
                <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold ${wizardStep >= 2 ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'}`}>2</span>
                Add your profile
              </span>
            </div>

            {/* STEP 1 — Audit context picker (status + objective + tone).
                Applied to both PDF and manual-wizard flows so the /analyze
                pipeline can tilt keywords, headlines and recommendations
                toward the user's actual goal. */}
            {wizardStep === 1 && (
              <>
                <div ref={contextPickerRef} className="rounded-2xl transition-shadow duration-200 mt-4" data-testid="dashboard-step-1">
                  <AuditContextPicker
                    status={auditStatus}
                    setStatus={(v) => { setAuditStatus(v); markContextTouched(); }}
                    objective={auditObjective}
                    setObjective={(v) => { setAuditObjective(v); markContextTouched(); }}
                    slider={auditSlider}
                    setSlider={(v) => { setAuditSlider(v); markContextTouched(); }}
                    disabled={uploadingPdf}
                    glow={!contextTouched}
                  />
                </div>
                <div className="mt-4 flex flex-wrap items-center justify-between gap-3 max-w-3xl">
                  <p className="text-xs text-muted-foreground">
                    We&rsquo;ll tune every keyword, headline and rewrite to this goal.
                  </p>
                  <Button
                    onClick={() => { markContextTouched(); setWizardStep(2); }}
                    disabled={uploadingPdf}
                    className="rounded-xl min-w-[160px]"
                    data-testid="dashboard-step-1-continue"
                  >
                    Continue <ArrowRight size={14} className="ml-1.5" />
                  </Button>
                </div>
              </>
            )}

            {/* STEP 2 — Upload PDF / manual + consent. Shows a summary chip
                of the goal picked in Step 1 with a one-click Edit back to
                Step 1 so users can course-correct without losing state. */}
            {wizardStep === 2 && (
              <div
                className={`mt-4 flex flex-wrap items-center justify-between gap-3 max-w-3xl rounded-2xl border-l-4 border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-950 px-4 py-3 shadow-sm ${
                  {
                    student_fresher: 'border-l-purple-500',
                    founder: 'border-l-orange-500',
                    corporate: 'border-l-teal-500',
                  }[auditStatus] || 'border-l-primary'
                }`}
                data-testid="dashboard-step-2-summary"
              >
                <div className="min-w-0">
                  <p className="text-[10px] font-mono-edge uppercase tracking-[0.16em] text-muted-foreground mb-0.5">Your goal</p>
                  <p className="text-sm font-medium truncate">
                    {_statusLabel} &middot; {_objectiveLabel}
                    <span className="ml-2 text-[10px] font-mono-edge uppercase tracking-[0.14em] text-muted-foreground">
                      Tone: {_sliderLabel}
                    </span>
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setWizardStep(1)}
                  className="shrink-0"
                  data-testid="dashboard-step-2-edit-goal"
                >
                  <Pencil size={12} className="mr-1" /> Edit
                </Button>
              </div>
            )}

            {wizardStep === 2 && (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-6 max-w-3xl">
              <TabsList className="bg-secondary rounded-xl p-1" data-testid="dashboard-input-tabs">
                <TabsTrigger value="pdf" className="rounded-lg data-[state=active]:bg-card data-[state=active]:shadow-sm" data-testid="dashboard-tab-pdf">
                  <FileUp size={14} className="mr-1.5" /> Upload PDF
                </TabsTrigger>
                <TabsTrigger value="manual" className="rounded-lg data-[state=active]:bg-card data-[state=active]:shadow-sm" data-testid="dashboard-tab-manual">
                  <Pencil size={14} className="mr-1.5" /> Type manually
                </TabsTrigger>
              </TabsList>

              <TabsContent value="pdf" className="mt-4">
                {pendingPdf && (
                  <div
                    className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-primary/40 bg-primary/5 px-4 py-3.5"
                    data-testid="dashboard-pending-pdf-card"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary shrink-0">
                        <FileText size={16} />
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate" data-testid="dashboard-pending-pdf-name">
                          Pre-loaded from your free audit: {pendingPdf.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Run your full audit with the same file — no re-upload needed.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        className="rounded-xl"
                        disabled={uploadingPdf}
                        onClick={() => {
                          if (!preflightUpload()) return;
                          const f = pendingPdf.file;
                          clearPendingFreeAuditPdf();
                          setPendingPdf(null);
                          handlePdfFile(f);
                        }}
                        data-testid="dashboard-pending-pdf-run"
                      >
                        Run full audit <ArrowRight size={13} className="ml-1.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { clearPendingFreeAuditPdf(); setPendingPdf(null); }}
                        data-testid="dashboard-pending-pdf-dismiss"
                      >
                        Dismiss
                      </Button>
                    </div>
                  </div>
                )}
                <LinkedInExportGuide />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={onFilePick}
                  className="hidden"
                  data-testid="dashboard-pdf-file-input"
                />
                <div
                  onClick={() => {
                    if (uploadingPdf) return;
                    if (!preflightUpload()) return;
                    fileInputRef.current?.click();
                  }}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => {
                    // Always intercept the file drop — never silently
                    // succeed and never let it start uploading before
                    // status/goal + consent are locked in.
                    if (!preflightUpload()) {
                      e.preventDefault(); setDragOver(false);
                      return;
                    }
                    onDrop(e);
                  }}
                  className={`group cursor-pointer rounded-2xl border-2 border-dashed transition-all p-8 text-center bg-card/70 ${
                    dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/60 hover:bg-card'
                  } ${uploadingPdf ? 'opacity-60 cursor-progress' : ''} ${(!consent.analysis || !contextTouched) ? 'border-amber-500/60 bg-amber-500/[0.04]' : ''}`}
                  data-testid="dashboard-pdf-dropzone"
                >
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-primary mb-3 group-hover:scale-105 transition-transform">
                    {uploadingPdf ? <Loader2 size={22} className="animate-spin" /> : <Upload size={22} />}
                  </div>
                  <p className="text-base font-semibold" data-testid="dashboard-pdf-upload-stage-label">
                    {uploadingPdf ? UPLOAD_STAGES[uploadStage].label : 'Drop your LinkedIn PDF here'}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1" data-testid="dashboard-pdf-upload-stage-sub">
                    {uploadingPdf ? UPLOAD_STAGES[uploadStage].sub : 'or click to choose a file. Max 10MB.'}
                  </p>
                  {uploadingPdf && (
                    <div className="mt-2.5 inline-flex gap-1" data-testid="dashboard-pdf-upload-dots">
                      {UPLOAD_STAGES.map((_, i) => (
                        <span
                          key={i}
                          className={`h-1.5 w-6 rounded-full transition-colors ${i <= uploadStage ? 'bg-primary' : 'bg-primary/20'}`}
                        />
                      ))}
                    </div>
                  )}
                  {!contextTouched && (
                    <p className="text-[11.5px] text-amber-700 dark:text-amber-400 mt-2 font-medium inline-flex items-center gap-1" data-testid="dashboard-pdf-context-hint">
                      <AlertTriangle size={11} /> Please pick &ldquo;You are&rdquo; and your Audit goal above first.
                    </p>
                  )}
                  {contextTouched && !consent.analysis && (
                    <p className="text-[11.5px] text-amber-700 dark:text-amber-400 mt-2 font-medium inline-flex items-center gap-1" data-testid="dashboard-pdf-consent-hint">
                      <AlertTriangle size={11} /> Please tick the consent checkbox below to enable upload.
                    </p>
                  )}
                  <Button type="button" variant="secondary" size="sm" className="mt-4" disabled={uploadingPdf} data-testid="dashboard-pdf-upload-button">
                    Choose PDF
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="manual" className="mt-4">
                <div className="rounded-2xl border border-border bg-card/70 p-6">
                  <Pencil size={20} className="text-primary mb-2" />
                  <p className="text-base font-semibold">Paste your profile details yourself.</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    A guided 6-step wizard. About 3-4 minutes.
                  </p>
                  <Button
                    onClick={() => {
                      if (!preflightUpload()) return;
                      navigate('/wizard', { state: { auditContext: { status: STATUS_TO_BACKEND[auditStatus] || auditStatus, objective: auditObjective, language_slider: auditSlider }, consent } });
                    }}
                    className="mt-4 rounded-xl"
                    data-testid="dashboard-skip-to-manual"
                  >
                    Open manual wizard <ChevronRight size={14} className="ml-1" />
                  </Button>
                  {!contextTouched && (
                    <p className="text-[11.5px] text-amber-700 dark:text-amber-400 mt-2 font-medium inline-flex items-center gap-1">
                      <AlertTriangle size={11} /> Pick &ldquo;You are&rdquo; and your Audit goal above first.
                    </p>
                  )}
                </div>
              </TabsContent>
            </Tabs>
            )}

            {/* Single-line consent — matches the Free Audit hero pattern.
                One combined checkbox: T&C + Privacy + profile ownership.
                Required before upload/manual-wizard is enabled. Only
                visible on Step 2 alongside the upload / manual entry
                point so it doesn't distract from the goal picker in
                Step 1. */}
            {wizardStep === 2 && (
            <label
              ref={consentBoxRef}
              className={`mt-6 flex items-start gap-2.5 rounded-xl border px-3.5 py-3 text-[12.5px] leading-snug cursor-pointer transition-all max-w-3xl ${
                consent.analysis
                  ? 'border-emerald-500/40 bg-emerald-500/[0.06] text-emerald-700 dark:text-emerald-300'
                  : 'border-primary/60 bg-primary/[0.04] text-foreground'
              }`}
              data-testid="dashboard-consent-block"
            >
              <input
                type="checkbox"
                checked={!!consent.analysis}
                onChange={(e) => setConsent({ ...consent, analysis: e.target.checked })}
                className="mt-0.5 h-4 w-4 shrink-0 rounded accent-primary"
                data-testid="dashboard-consent-checkbox"
                aria-required="true"
              />
              <span>
                I agree to the{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-primary">Terms &amp; Conditions</a>{' '}
                &amp;{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-primary">Privacy Policy</a>{' '}
                and confirm that this profile belongs to me or that I am authorised by the profile owner to submit it for analysis and audit.
              </span>
            </label>
            )}
          </CardContent>
        </Card>

        {/* Score trajectory chart — renders only for users with 2+
            scored audits so brand-new users aren't shown a lonely dot. */}
        <ScoreTimeline history={filteredHistory} currentUserId={user?.id} />

        {/* History */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold inline-flex items-center gap-2">
                <HistoryIcon size={18} /> {isAdminOrEditor ? 'All saved analyses' : 'Your analyses'}
              </h2>
              {isAdminOrEditor && (
                <p className="text-xs text-muted-foreground mt-1">
                  As <span className="font-mono-edge">{historyRole}</span>, you can view every saved profile analysis across the team.
                </p>
              )}
              {ownerFilter && (
                <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-primary/10 border border-primary/30 px-3 py-1" data-testid="history-owner-filter-chip">
                  <span className="text-[11px] font-medium text-primary">
                    Showing analyses by <span className="font-semibold">{filterLabel}</span>
                  </span>
                  <button
                    type="button"
                    onClick={clearOwnerFilter}
                    className="text-primary hover:text-primary/70 transition-colors"
                    aria-label="Clear filter"
                    data-testid="history-owner-filter-clear"
                  >
                    <X size={12} />
                  </button>
                </div>
              )}
              {isAdminOrEditor && legacyCount > 0 && (
                <button
                  type="button"
                  onClick={() => setHideLegacy((v) => !v)}
                  className={[
                    'ml-2 mt-2 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 transition-colors',
                    hideLegacy
                      ? 'bg-primary/10 border-primary/40 text-primary hover:bg-primary/15'
                      : 'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-400 hover:bg-amber-500/15',
                  ].join(' ')}
                  data-testid="history-legacy-filter-toggle"
                  title={hideLegacy
                    ? `${legacyCount} legacy audit${legacyCount === 1 ? '' : 's'} hidden — click to include`
                    : `${legacyCount} legacy audit${legacyCount === 1 ? '' : 's'} in view — click to hide`}
                >
                  <span className={[
                    'inline-block w-1.5 h-1.5 rounded-full',
                    hideLegacy ? 'bg-primary' : 'bg-amber-500',
                  ].join(' ')} />
                  <span className="text-[11px] font-medium font-mono-edge tracking-wide uppercase">
                    {hideLegacy ? 'Legacy hidden' : `Hide legacy (${legacyCount})`}
                  </span>
                </button>
              )}
            </div>
            <span className="text-sm text-muted-foreground" data-testid="history-count">
              {ownerFilter
                ? `${filteredHistory.length} of ${history.length}`
                : `${history.length} total`}
            </span>
          </div>
          {filteredHistory.length === 0 ? (
            <Card className="border border-dashed border-purple-300/50 dark:border-white/10 bg-white/40 dark:bg-slate-900/40 backdrop-blur-xl rounded-3xl">
              <CardContent className="p-8 text-center text-muted-foreground">
                <FileText size={28} className="mx-auto mb-3 opacity-70" />
                {ownerFilter
                  ? 'No analyses match this filter.'
                  : 'No analyses yet. Start one above — it takes 2-5 minutes.'}
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="history-list">
                {pagedHistory.map((h) => (
                  <Card
                    key={h.id}
                    className={[
                      'transition-colors',
                      h.is_legacy
                        ? 'border-l-4 border-l-amber-500 border-y border-r border-y-amber-500/25 border-r-amber-500/25 bg-amber-50/40 hover:border-amber-500/70'
                        : 'border-border hover:border-primary/40',
                    ].join(' ')}
                    data-testid="history-item"
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-semibold truncate" title={h.profile_name}>{h.profile_name || 'Untitled'}</h3>
                        {statusBadge(h.status)}
                      </div>
                      {h.is_legacy && (
                        <span
                          className="mt-1 inline-flex items-center rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[9.5px] font-mono-edge tracking-[0.12em] uppercase text-amber-600 dark:text-amber-400"
                          data-testid="history-legacy-chip"
                          title="Created with the retired URL-import flow — newer audits use PDF uploads, so results may look different."
                        >
                          Legacy analysis
                        </span>
                      )}
                      {h.audit_context?.objective && (
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-1" data-testid="history-audit-context">
                          {String(h.audit_context.status || '').replace('_', '/')}
                          {' · '}
                          {String(h.audit_context.objective).replace(/_/g, ' ')}
                        </p>
                      )}
                      {isAdminOrEditor && h.owner_email && (
                        <div className="mt-1 flex items-center gap-1.5">
                          {h.is_own ? (
                            <Badge variant="secondary" className="font-mono-edge text-[10px]" data-testid="history-owner-own">You</Badge>
                          ) : (
                            <Badge variant="secondary" className="font-mono-edge text-[10px]" data-testid="history-owner-other">
                              {h.owner_name || h.owner_email}
                            </Badge>
                          )}
                          {!h.is_own && (
                            <span className="text-[10px] text-muted-foreground truncate" title={h.owner_email}>{h.owner_email}</span>
                          )}
                        </div>
                      )}
                      {h.profile_url && (
                        <p className="text-xs text-muted-foreground truncate mt-1" title={h.profile_url}>
                          {h.profile_url}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground mt-2">
                        {h.created_at ? format(new Date(h.created_at), 'MMM d, yyyy • h:mm a') : ''}
                      </p>
                      <div className="mt-4 flex items-center justify-between gap-2">
                        <Button size="sm" variant="secondary" onClick={() => navigate(`/analysis/${h.id}`)} data-testid="history-open-button">
                          Open
                        </Button>
                        <div className="flex items-center gap-1.5">
                          {isAdminOrEditor && h.status === 'complete' && (
                            <BrandedReportButton
                              analysisId={h.id}
                              label={`${h.profile_name || 'audit'}_${(h.owner_name || h.owner_email || 'user').split('@')[0]}`}
                              size="sm"
                              isLegacy={!!h.is_legacy}
                              onConverted={loadHistory}
                            />
                          )}
                          {(user?.role === 'admin' || h.is_own !== false) && (
                            <Button size="icon" variant="ghost" onClick={() => onDelete(h.id)} aria-label="Delete" data-testid="history-delete-button">
                              <Trash2 size={16} />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              {/* Pagination footer — only shows when there is more than one page. */}
              {totalPages > 1 && (
                <div
                  className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                  data-testid="history-pagination"
                >
                  <span
                    className="text-xs text-muted-foreground font-mono-edge tracking-wide"
                    data-testid="history-page-summary"
                  >
                    Showing {Math.min(filteredHistory.length, (historyPage - 1) * HISTORY_PAGE_SIZE + 1)}
                    {'–'}
                    {Math.min(filteredHistory.length, historyPage * HISTORY_PAGE_SIZE)} of {filteredHistory.length}
                  </span>
                  <Pagination className="mx-0 justify-end">
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            if (historyPage > 1) setHistoryPage(historyPage - 1);
                          }}
                          className={historyPage <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                          data-testid="history-page-prev"
                        />
                      </PaginationItem>
                      {pageNumbers.map((p, i) =>
                        p === '…' ? (
                          <PaginationItem key={`gap-${i}`}>
                            <PaginationEllipsis />
                          </PaginationItem>
                        ) : (
                          <PaginationItem key={p}>
                            <PaginationLink
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                setHistoryPage(p);
                              }}
                              isActive={historyPage === p}
                              data-testid={`history-page-${p}`}
                              className="cursor-pointer"
                            >
                              {p}
                            </PaginationLink>
                          </PaginationItem>
                        ),
                      )}
                      <PaginationItem>
                        <PaginationNext
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            if (historyPage < totalPages) setHistoryPage(historyPage + 1);
                          }}
                          className={historyPage >= totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                          data-testid="history-page-next"
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </>
          )}
        </section>

        {/* Bottom-of-page soft upsell (renders only for one_time-active users). */}
        <PlanHintBanner position="bottom" />
      </div>
      <BlayzBot
        context="dashboard"
        state={{
          uploading: uploadingPdf,
          hasPendingPdf: !!pendingPdf,
          consentGiven: !!consent.analysis,
          historyCount: history?.length || 0,
        }}
      />
      {/* Hard blocker popup — fires when a user attempts to upload/drop
          a PDF or open the manual wizard before ticking status + goal
          AND the required consent. The CTA scrolls the missing field
          into view and pulses a ring around it. */}
      <AlertDialog open={blocker.open} onOpenChange={(o) => setBlocker({ open: o, kind: blocker.kind })}>
        <AlertDialogContent data-testid="dashboard-blocker-dialog">
          <AlertDialogHeader>
            <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-500/15 text-amber-600 mb-1">
              <AlertTriangle size={22} />
            </div>
            <AlertDialogTitle className="text-center">
              {blocker.kind === 'context'
                ? 'Almost — pick your status and goal first'
                : 'One quick tick before we upload'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center leading-relaxed">
              {blocker.kind === 'context' ? (
                <>
                  We tailor every recommendation to <strong>who you are</strong> (Student / Fresher / Corporate) and <strong>what you want out of this audit</strong>. Pick both above and we&rsquo;ll score you against the right benchmark.
                </>
              ) : (
                <>
                  Please tick the consent box below to confirm you own this profile (or are authorised to submit it) and agree to our Terms &amp; Privacy Policy. It only takes a second and it&rsquo;s a legal requirement.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={focusMissingField} data-testid="dashboard-blocker-cta">
              {blocker.kind === 'context' ? 'Take me to the picker' : 'Take me to the consent'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
